import React, { useState, useEffect } from 'react';
import { 
  Card, Tabs, Table, Tag, Button, Space, 
  Transfer, Modal, Spin, notification, Alert, 
  Typography, Divider
} from 'antd';
import { 
  UserOutlined, TeamOutlined, LockOutlined, 
  SaveOutlined, ReloadOutlined,AlertFilled
} from '@ant-design/icons';
import userRolePermissionService from '../../../services/userRolePermissionService';
import roleService from '../../../services/roleService';
import permissionService from '../../../services/permissionService';
import userRoleUtils from '../../../utils/userRoleUtils';
import MyTable from '../../../components/common/MyTable';
 
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const UserRolesPermissions = ({ userId, userName ,viewOnly}) => {
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [adminRoles, setAdminRoles] = useState([]);
  const [regularRoles, setRegularRoles] = useState([]);
  const [selectedRoleKeys, setSelectedRoleKeys] = useState([]);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Check if user has permission to manage user roles
  const canManageUserRoles = (userRoleUtils.hasPermission('manage_user_roles') || userRoleUtils.isAdmin()) && !viewOnly;

  // Load data on component mount
  useEffect(() => {
    if (userId) {
      fetchUserData();
      fetchRoles();
    }
  }, [userId]);

  // Fetch user roles and permissions
  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Fetch user roles
      const rolesResponse = await userRolePermissionService.getUserRoles(userId);
      setUserRoles(rolesResponse?.data?.roles || []);
      
      // Set initial selected role keys
      setSelectedRoleKeys(rolesResponse.data?.roles?.map(role => role.id) || []);
      
      // Fetch user permissions
      const permissionsResponse = await userRolePermissionService.getUserPermissions(userId);
      setUserPermissions(permissionsResponse?.data?.permissions || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching user data',
        description: error.message || 'Failed to load user roles and permissions'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all roles and separate admin/regular roles
  const fetchRoles = async () => {
    try {
      const response = await roleService.getAllRoles();
      const allRoles = response.data.data || [];
      
      setAllRoles(allRoles);
      setAdminRoles(allRoles.filter(role => role.is_admin === 1));
      setRegularRoles(allRoles.filter(role => role.is_admin !== 1));
    } catch (error) {
      notification.error({
        message: 'Error fetching roles',
        description: error.message || 'Failed to load roles'
      });
    }
  };

  // Handle role assignment
  const handleRoleAssignment = async () => {
    try {
      setSaveLoading(true);
      
      await userRolePermissionService.assignUserRoles(userId, selectedRoleKeys);
      
      notification.success({
        message: 'Roles updated successfully',
        description: `Roles for ${userName} have been updated.`
      });
      
      setRoleModalVisible(false);
      fetchUserData(); // Refresh data
    } catch (error) {
      notification.error({
        message: 'Error updating roles',
        description: error.message || 'Failed to update user roles'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle transfer change
  const handleTransferChange = (nextTargetKeys) => {
    setSelectedRoleKeys(nextTargetKeys);
  };

  // Group permissions by category and admin status
  const groupedPermissions = userPermissions.reduce((groups, permission) => {
    const group = permission.group || 'General';
    
    if (!groups[group]) {
      groups[group] = {
        admin: [],
        regular: []
      };
    }
    
    if (permission.is_admin === 1) {
      groups[group].admin.push(permission);
    } else {
      groups[group].regular.push(permission);
    }
    
    return groups;
  }, {});

  // Table columns for roles
  const roleColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <div className="font-medium">
            {record.is_admin === 1 && <AlertFilled className="mr-1 text-red-500" />}
            {record.display_name || name}
          </div>
          <div className="text-xs text-gray-500">{name}</div>
        </div>
      )
    },
    {
      title: 'Type',
      key: 'is_admin',
      render: (_, record) => (
        record.is_admin === 1 ? (
          <Tag color="red" icon={<AlertFilled />}>Admin</Tag>
        ) : (
          <Tag color="blue" icon={<LockOutlined />}>Regular</Tag>
        )
      ),
      width: 120
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_, record) => (
        <div>
          {userPermissions?.length ? (
            <>
              {userPermissions.slice(0, 3).map(perm => (
                <Tag 
                  key={perm.id} 
                  color={perm.is_admin === 1 ? "red" : "blue"}
                >
                  {perm.name}
                </Tag>
              ))}
              {userPermissions.length > 3 && (
                <Tag color="default">+{userPermissions.length - 3} more</Tag>
              )}
            </>
          ) : (
            <span className="text-gray-400">No permissions</span>
          )}
        </div>
      )
    }
  ];

  // Table columns for permissions
  const permissionColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          {record.is_admin === 1 && <AlertFilled className="mr-1 text-red-500" />}
          {name}
        </div>
      )
    },
    {
      title: 'Type',
      key: 'is_admin',
      render: (_, record) => (
        record.is_admin === 1 ? (
          <Tag color="red" icon={<AlertFilled />}>Admin</Tag>
        ) : (
          <Tag color="blue" icon={<LockOutlined />}>Regular</Tag>
        )
      ),
      width: 120
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    
    
  ];

  return (
    <div className="mt-4">
      <Card className="shadow-sm">
        <Tabs defaultActiveKey="roles">
          <TabPane 
            tab={<span><TeamOutlined /> Roles</span>}
            key="roles"
          >
            <div className="flex justify-between items-center mb-4">
              <Title level={4}>User Roles</Title>
              <Space>
                <Button 
                  type="primary" 
                  icon={<TeamOutlined />} 
                  onClick={() => setRoleModalVisible(true)}
                  disabled={!canManageUserRoles}
                  className="bg-golden-navy"
                >
                  Manage Roles
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchUserData}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <Spin size="large" />
              </div>
            ) : (
              <>
                {userRoles.length > 0 ? (
                  <MyTable 
                    rowKey="id"
                    columns={roleColumns}
                    dataSource={userRoles}
                     bordered
                    rowClassName={record => record.is_admin === 1 ? 'bg-red-50' : ''}
                  />
                ) : (
                  <Alert 
                    message="No Roles Assigned" 
                    description="This user doesn't have any roles assigned yet."
                    type="info" 
                    showIcon 
                  />
                )}
              </>
            )}
          </TabPane>
          
          <TabPane 
            tab={<span><LockOutlined /> Permissions</span>}
            key="permissions"
          >
            <div className="mb-4">
              <Title level={4}>User Permissions</Title>
              <Text type="secondary">
                Permissions are inherited from assigned roles. To modify permissions, please update the user's roles.
              </Text>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <Spin size="large" />
              </div>
            ) : (
              <>
                {userPermissions.length > 0 ? (
                  <div>
                    {/* Admin Permissions Section */}
                    {userPermissions.some(p => p.is_admin === 1) && (
                      <div className="mb-6">
                        <Divider orientation="left">
                          <AlertFilled className="mr-1 text-red-500" />
                          Admin Permissions
                        </Divider>
                        <div className="flex flex-wrap gap-2">
                          {userPermissions
                            .filter(p => p.is_admin === 1)
                            .map(permission => (
                              <Tag key={permission.id} color="red" icon={<AlertFilled />}>
                                {permission.name}
                              </Tag>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Regular Permissions Section by Group */}
                    {Object.entries(groupedPermissions).map(([group, permissions]) => (
                      <div key={group} className="mb-6">
                        <Divider orientation="left">{group}</Divider>
                        
                        {permissions.regular.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {permissions.regular.map(permission => (
                              <Tag key={permission.id} color="blue">
                                {permission.name}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    <Divider />
                    
                    <MyTable 
                      rowKey="id"
                      columns={permissionColumns}
                      dataSource={userPermissions}
                     
                      bordered
                      rowClassName={record => record.is_admin === 1 ? 'bg-red-50' : ''}
                    />
                  </div>
                ) : (
                  <Alert 
                    message="No Permissions" 
                    description="This user doesn't have any permissions yet. Assign roles to grant permissions."
                    type="info" 
                    showIcon 
                  />
                )}
              </>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Role Assignment Modal */}
      <Modal
        title={`Manage Roles for ${userName}`}
        width={800}
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setRoleModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            icon={<SaveOutlined />}
            loading={saveLoading}
            onClick={handleRoleAssignment}
            className="bg-golden-navy"
          >
            Save Roles
          </Button>
        ]}
      >
        <div className="mb-4">
          <Alert
            type="info"
            showIcon
            message="Role Assignment"
            description={
              <ul className="mt-2 ml-2">
                <li><strong>Admin Roles:</strong> Grant access to administrative features and admin permissions.</li>
                <li><strong>Regular Roles:</strong> Grant access to regular user features and regular permissions.</li>
              </ul>
            }
            className="mb-4"
          />
          <p>
            Select the roles to assign to this user. The user will inherit all permissions from the assigned roles.
          </p>
        </div>
        
        <div className="mb-4">
          <Divider orientation="left">
            <AlertFilled className="mr-1 text-red-500" />
            Admin Roles
          </Divider>
          <Transfer
            dataSource={adminRoles.map(role => ({
              key: role.id,
              title: role.display_name || role.name,
              description: role.description,
              disabled: false
            }))}
            titles={['Available', 'Assigned']}
            targetKeys={selectedRoleKeys.filter(id => 
              adminRoles.some(role => role.id === id)
            )}
            onChange={(nextTargetKeys) => {
              // Update only admin role selections
              const currentRegularRoleKeys = selectedRoleKeys.filter(id => 
                regularRoles.some(role => role.id === id)
              );
              setSelectedRoleKeys([...currentRegularRoleKeys, ...nextTargetKeys]);
            }}
            render={item => (
              <div>
                <div className="font-medium">
                  <AlertFilled className="mr-1 text-red-500" />
                  {item.title}
                </div>
                <div className="text-xs text-gray-500 truncate" title={item.description}>
                  {item.description}
                </div>
              </div>
            )}
            listStyle={{
              width: 350,
              height: 200,
            }}
            operations={['→', '←']}
            showSearch
            filterOption={(inputValue, item) =>
              item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
              item.description.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
        </div>
        
        <div>
          <Divider orientation="left">
            <LockOutlined className="mr-1 text-blue-500" />
            Regular Roles
          </Divider>
          <Transfer
            dataSource={regularRoles.map(role => ({
              key: role.id,
              title: role.display_name || role.name,
              description: role.description,
              disabled: false
            }))}
            titles={['Available', 'Assigned']}
            targetKeys={selectedRoleKeys.filter(id => 
              regularRoles.some(role => role.id === id)
            )}
            onChange={(nextTargetKeys) => {
              // Update only regular role selections
              const currentAdminRoleKeys = selectedRoleKeys.filter(id => 
                adminRoles.some(role => role.id === id)
              );
              setSelectedRoleKeys([...currentAdminRoleKeys, ...nextTargetKeys]);
            }}
            render={item => (
              <div>
                <div className="font-medium">{item.title}</div>
                <div className="text-xs text-gray-500 truncate" title={item.description}>
                  {item.description}
                </div>
              </div>
            )}
            listStyle={{
              width: 350,
              height: 300,
            }}
            operations={['→', '←']}
            showSearch
            filterOption={(inputValue, item) =>
              item.title.toLowerCase().includes(inputValue.toLowerCase()) ||
              item.description.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default UserRolesPermissions;